import React, { useMemo } from 'react';
import { Progress, Typography } from 'antd';
import dayjs from 'dayjs';
import { MoneyCell } from '../../ui';
import { obligationStageLabel } from '../../utils/obligationStageUi';

const { Text } = Typography;

export interface FinanceProgressByStageRow {
  stage: string;
  total: number;
}

export interface FinanceProgressCardProps {
  type: 'incoming' | 'outgoing';
  total: number;
  paid: number;
  /** ISO или распознаваемая dayjs дата; пусто — блок срока не показываем */
  dueDate: string | null;
  /** Разбивка по стадиям (PREPAYMENT / FINAL и т.д.) — мелкий текст под прогрессом */
  byStage?: FinanceProgressByStageRow[];
}

const INCOMING = '#52c41a';
const OUTGOING = '#fa8c16';
const OVERDUE = '#ff4d4f';

function clampPercent(p: number): number {
  if (Number.isNaN(p) || !Number.isFinite(p)) return 0;
  return Math.min(100, Math.max(0, p));
}

const FinanceProgressCard: React.FC<FinanceProgressCardProps> = ({
  type,
  total,
  paid,
  dueDate,
  byStage,
}) => {
  const baseColor = type === 'incoming' ? INCOMING : OUTGOING;
  const title = type === 'incoming' ? '⬇ Получить' : '⬆ Оплатить';

  const { daysLeft, accentColor } = useMemo(() => {
    const trimmed = dueDate?.trim();
    if (!trimmed) {
      return { daysLeft: null as number | null, accentColor: baseColor };
    }
    const due = dayjs(trimmed).startOf('day');
    if (!due.isValid()) {
      return { daysLeft: null as number | null, accentColor: baseColor };
    }
    const today = dayjs().startOf('day');
    const dl = due.diff(today, 'day');
    return {
      daysLeft: dl,
      accentColor: dl < 0 ? OVERDUE : baseColor,
    };
  }, [dueDate, baseColor]);

  const progressPercent = useMemo(() => {
    if (total <= 0) return paid > 0 ? 100 : 0;
    return clampPercent((paid / total) * 100);
  }, [total, paid]);

  const dueLine = useMemo(() => {
    const trimmed = dueDate?.trim();
    if (!trimmed) return null;
    const due = dayjs(trimmed);
    if (!due.isValid()) return null;
    const dateStr = due.format('DD.MM.YYYY');
    if (daysLeft === null) return <Text style={{ fontSize: 12 }}>{dateStr}</Text>;
    if (daysLeft < 0) {
      return (
        <Text style={{ fontSize: 12, color: OVERDUE }}>
          {dateStr} · просрочено на {Math.abs(daysLeft)} дн.
        </Text>
      );
    }
    if (daysLeft === 0) {
      return (
        <Text style={{ fontSize: 12, color: accentColor }}>
          {dateStr} · сегодня
        </Text>
      );
    }
    return (
      <Text style={{ fontSize: 12, color: accentColor }}>
        {dateStr} · осталось {daysLeft} дн.
      </Text>
    );
  }, [dueDate, daysLeft, accentColor]);

  return (
    <div style={{ minWidth: 200, maxWidth: 280 }}>
      <Text strong style={{ fontSize: 12, color: accentColor, display: 'block', marginBottom: 6 }}>
        {title}
      </Text>
      <Progress
        percent={progressPercent}
        showInfo={false}
        strokeColor={accentColor}
        trailColor="var(--border-color-split, rgba(0,0,0,0.06))"
        size="small"
      />
      <div style={{ marginTop: 4, marginBottom: 2 }}>
        <Text style={{ fontSize: 12 }}>
          <MoneyCell value={paid} type={type === 'incoming' ? 'income' : 'expense'} />
          <Text type="secondary"> / </Text>
          <MoneyCell value={total} type={type === 'incoming' ? 'income' : 'expense'} />
        </Text>
      </div>
      {byStage && byStage.length > 0 && (
        <div style={{ marginTop: 4, marginBottom: 2 }}>
          {byStage.map((row, idx) => (
            <Text
              key={`${row.stage}-${idx}`}
              type="secondary"
              style={{ fontSize: 11, lineHeight: 1.35, display: 'block' }}
            >
              {obligationStageLabel(row.stage)}:{' '}
              <MoneyCell value={row.total} type={type === 'incoming' ? 'income' : 'expense'} />
            </Text>
          ))}
        </div>
      )}
      {dueLine}
    </div>
  );
};

export default FinanceProgressCard;
